/*
 * Cart add-ons (data-driven)
 * --------------------------
 * Builds the add-on row from Shopify's own product recommendations, based on
 * what's currently in the cart:
 *   1. read the cart (/cart.js)
 *   2. for each cart product, fetch /recommendations/products?intent=complementary
 *      (falling back to intent=related) as JSON
 *   3. merge + dedupe, drop items already in cart, out of stock, or >= price cap
 *   4. if nothing came back (little order data yet), use the fallback products
 *      emitted by the Liquid snippet from the chosen collection
 *   5. render cards; single-variant -> Ajax add, multi-variant -> link to PDP
 *
 * Perf/UX:
 *   - Skeleton cards render immediately on open so the row never appears as a
 *     blank gap while the network calls resolve.
 *   - Recommendations are preloaded (warmed into CACHE) as soon as this script
 *     boots and again after every cart refresh, so by the time the drawer opens
 *     the data is usually already in memory.
 *
 * Also handles: Ajax add + drawer refresh, and desktop arrow navigation.
 * Survives the cart drawer's Ajax re-render (delegated clicks + observer).
 * No external libraries.
 */
(function () {
  var CACHE = {};        // product_id -> recommendations array (per page load)
  var isRendering = false; // guard so our own DOM writes don't retrigger the observer
  var debounceTimer = null;
  var moneyFormat = null;  // resolved once from the store
  var lastSkeletonCount = 0;

  function getCartDrawer() { return document.querySelector('cart-drawer'); }
  function root() { return document.querySelector('[data-cart-addons]'); }

  // Escape any string before it is placed into innerHTML (prevents broken
  // markup / injection from product titles, urls, etc.).
  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function resolveMoneyFormat() {
    if (moneyFormat !== null) return;
    moneyFormat =
      (window.Shopify && window.Shopify.money_format) ||
      '';
  }
  function money(cents) {
    resolveMoneyFormat();
    try {
      if (window.Shopify && typeof Shopify.formatMoney === 'function' && moneyFormat) {
        return Shopify.formatMoney(cents, moneyFormat);
      }
    } catch (e) {}
    try {
      var cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'SEK';
      return new Intl.NumberFormat(document.documentElement.lang || 'sv-SE', {
        style: 'currency', currency: cur
      }).format(cents / 100);
    } catch (e2) {}
    return (cents / 100).toFixed(2).replace('.', ',') + ' kr';
  }

  function fetchJSON(url) {
    return fetch(url, { headers: { Accept: 'application/json' } }).then(function (r) {
      if (!r.ok) throw new Error('bad response');
      return r.json();
    });
  }

  function recommendationsFor(productId, limit) {
    if (CACHE[productId]) return Promise.resolve(CACHE[productId]);
    var base = window.Shopify.routes.root + 'recommendations/products.json';
    var comp = base + '?product_id=' + productId + '&limit=' + limit + '&intent=complementary';
    var rel = base + '?product_id=' + productId + '&limit=' + limit + '&intent=related';
    return fetchJSON(comp)
      .then(function (data) {
        if (data && data.products && data.products.length) return data.products;
        return fetchJSON(rel).then(function (d2) { return (d2 && d2.products) ? d2.products : []; });
      })
      .catch(function () {
        return fetchJSON(rel).then(function (d2) { return (d2 && d2.products) ? d2.products : []; })
          .catch(function () { return []; });
      })
      .then(function (products) { CACHE[productId] = products; return products; });
  }

  // Warm the cache from the current cart without touching the DOM. Runs on boot
  // and after cart refreshes so data is ready before the drawer is opened.
  function preloadRecommendations() {
    if (!window.Shopify || !window.Shopify.routes) return;
    fetchJSON(window.Shopify.routes.root + 'cart.js').then(function (cart) {
      if (!cart || !cart.items || !cart.items.length) return;
      var seen = {};
      cart.items.forEach(function (li) {
        if (seen[li.product_id]) return;
        seen[li.product_id] = true;
        recommendationsFor(li.product_id, 10);
      });
    }).catch(function () {});
  }

  function normaliseShopifyProduct(p) {
    var firstAvailable = null;
    if (p.variants && p.variants.length) {
      for (var i = 0; i < p.variants.length; i++) {
        if (p.variants[i].available) { firstAvailable = p.variants[i]; break; }
      }
    }
    var variant = firstAvailable || (p.variants && p.variants[0]) || null;
    var image = '';
    if (p.featured_image) image = p.featured_image;
    else if (p.images && p.images.length) image = p.images[0];
    return {
      id: p.id,
      title: p.title,
      url: p.url,
      price: variant ? variant.price : p.price,
      image: image,
      variant_id: variant ? variant.id : null,
      multi: (p.variants && p.variants.length > 1),
      available: !!variant
    };
  }

  function readFallback() {
    var el = document.querySelector('[data-addons-fallback]');
    if (!el) return [];
    try {
      var arr = JSON.parse(el.textContent);
      return arr.map(function (x) { x.available = true; return x; });
    } catch (e) { return []; }
  }

  // How many skeleton cards to show while loading. Prefer the count from the
  // last successful render; otherwise a sensible default bounded by max.
  function skeletonCount(cfg) {
    var n = lastSkeletonCount || 3;
    if (n > cfg.max) n = cfg.max;
    if (n < 1) n = 1;
    return n;
  }

  function buildSkeleton(cfg) {
    var r = root();
    if (!r) return;
    var track = r.querySelector('[data-addons-track]');
    if (!track) return;
    var n = skeletonCount(cfg);
    var html = '';
    for (var i = 0; i < n; i++) {
      html += '<div class="cart-addons__card cart-addons__card--skeleton" aria-hidden="true">' +
        '<span class="cart-addons__sk cart-addons__sk-media"></span>' +
        '<span class="cart-addons__sk cart-addons__sk-line"></span>' +
        '<span class="cart-addons__sk cart-addons__sk-price"></span>' +
        '<span class="cart-addons__sk cart-addons__sk-btn"></span>' +
        '</div>';
    }
    isRendering = true;
    track.innerHTML = html;
    r.hidden = false;
    setTimeout(function () { isRendering = false; }, 0);
  }

  function buildCards(items, cfg) {
    var track = root().querySelector('[data-addons-track]');
    track.innerHTML = '';
    items.forEach(function (it) {
      var card = document.createElement('div');
      card.className = 'cart-addons__card';

      var safeUrl = esc(it.url);
      var safeTitle = esc(it.title);

      var mediaHtml = '<a href="' + safeUrl + '" class="cart-addons__media" tabindex="-1" aria-hidden="true">' +
        (it.image ? '<img src="' + esc(it.image) + '" alt="" loading="lazy">' : '') + '</a>';

      var priceHtml = '<span class="cart-addons__price">' +
        (it.multi && cfg.fromLabel ? esc(cfg.fromLabel) + ' ' : '') + esc(money(it.price)) + '</span>';

      var actionHtml;
      if (it.multi || !it.variant_id) {
        actionHtml = '<a href="' + safeUrl + '" class="cart-addons__add">' + esc(cfg.button) + '</a>';
      } else {
        actionHtml = '<button type="button" class="cart-addons__add" data-cart-addon ' +
          'data-variant-id="' + esc(it.variant_id) + '" data-added-label="' + esc(cfg.buttonAdded) + '">' +
          esc(cfg.button) + '</button>';
      }

      card.innerHTML = mediaHtml +
        '<a href="' + safeUrl + '" class="cart-addons__name">' + safeTitle + '</a>' +
        priceHtml + actionHtml;
      track.appendChild(card);
    });
    lastSkeletonCount = items.length;
  }

  function readConfig() {
    var r = root();
    return {
      button: r.getAttribute('data-button') || 'Lägg till',
      buttonAdded: r.getAttribute('data-button-added') || 'Tillagd',
      // Text före pris för produkter med flera storlekar (marknadsstyrd i Liquid).
      fromLabel: r.hasAttribute('data-from') ? r.getAttribute('data-from') : 'från',
      max: parseInt(r.getAttribute('data-max'), 10) || 4,
      priceCap: parseInt(r.getAttribute('data-price-cap'), 10) || 0
    };
  }

  function renderAddons(showSkeletonFirst) {
    var r = root();
    if (!r) return;
    var cfg = readConfig();

    // Paint skeletons immediately so the row never flashes empty.
    if (showSkeletonFirst) buildSkeleton(cfg);

    fetchJSON(window.Shopify.routes.root + 'cart.js').then(function (cart) {
      if (!cart || !cart.items || !cart.items.length) {
        isRendering = true;
        r.hidden = true;
        var tr = r.querySelector('[data-addons-track]');
        if (tr) tr.innerHTML = '';
        setTimeout(function () { isRendering = false; }, 0);
        return;
      }

      var inCart = {};
      cart.items.forEach(function (li) { inCart[li.product_id] = true; });

      var productIds = [];
      cart.items.forEach(function (li) {
        if (productIds.indexOf(li.product_id) === -1) productIds.push(li.product_id);
      });

      Promise.all(productIds.map(function (pid) {
        return recommendationsFor(pid, 10);
      })).then(function (lists) {
        var seen = {};
        var merged = [];
        lists.forEach(function (list) {
          list.forEach(function (p) {
            var item = normaliseShopifyProduct(p);
            if (!item.available) return;
            if (inCart[item.id]) return;
            if (seen[item.id]) return;
            if (cfg.priceCap > 0 && item.price >= cfg.priceCap) return;
            seen[item.id] = true;
            merged.push(item);
          });
        });

        if (merged.length === 0) {
          readFallback().forEach(function (item) {
            if (inCart[item.id]) return;
            if (seen[item.id]) return;
            if (cfg.priceCap > 0 && item.price >= cfg.priceCap) return;
            seen[item.id] = true;
            merged.push(item);
          });
        }

        merged = merged.slice(0, cfg.max);

        isRendering = true;
        if (merged.length === 0) {
          r.hidden = true;
          var tr2 = r.querySelector('[data-addons-track]');
          if (tr2) tr2.innerHTML = '';
        } else {
          buildCards(merged, cfg);
          r.hidden = false;
          initAddonsNav();
        }
        setTimeout(function () { isRendering = false; }, 0);
      });
    }).catch(function () {
      isRendering = true;
      r.hidden = true;
      setTimeout(function () { isRendering = false; }, 0);
    });
  }

  function scheduleRender() {
    if (isRendering) return;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () { renderAddons(true); }, 120);
  }

  /* ---------- Ajax add (single-variant products) ---------- */

  function refreshCartDrawer() {
    var drawer = getCartDrawer();
    var sections = ['cart-drawer', 'cart-icon-bubble'];
    return fetch(window.Shopify.routes.root + '?sections=' + sections.join(','))
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data['cart-drawer']) {
          var parsed = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
          var freshInner = parsed.querySelector('.drawer__inner');
          var currentInner = drawer && drawer.querySelector('.drawer__inner');
          if (freshInner && currentInner) {
            currentInner.innerHTML = freshInner.innerHTML;
            drawer.classList.remove('is-empty');
          }
        }
        if (data['cart-icon-bubble']) {
          var parsedBubble = new DOMParser().parseFromString(data['cart-icon-bubble'], 'text/html');
          var freshBubble = parsedBubble.querySelector('.shopify-section');
          var currentBubble = document.getElementById('cart-icon-bubble');
          if (freshBubble && currentBubble) currentBubble.innerHTML = freshBubble.innerHTML;
        }
        renderAddons(true);
        preloadRecommendations();
      });
  }

  function addVariant(variantId, button) {
    var addedLabel = button.getAttribute('data-added-label') || 'Tillagd';
    var originalLabel = button.textContent;
    button.setAttribute('disabled', 'disabled');
    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    })
      .then(function (res) { if (!res.ok) throw new Error('add failed'); return res.json(); })
      .then(function () {
        button.textContent = addedLabel;
        button.classList.add('is-added');
        return refreshCartDrawer();
      })
      .catch(function () {
        button.removeAttribute('disabled');
        button.textContent = originalLabel;
      });
  }

  /* ---------- Arrow navigation ---------- */

  function updateArrows(track, prev, next) {
    var maxScroll = track.scrollWidth - track.clientWidth;
    var x = track.scrollLeft;
    if (prev) prev.disabled = x <= 2;
    if (next) next.disabled = x >= maxScroll - 2;
  }
  function stepAmount(track) {
    var card = track.querySelector('.cart-addons__card');
    if (!card) return track.clientWidth * 0.8;
    return card.getBoundingClientRect().width + 8;
  }
  function initAddonsNav() {
    var r = root();
    if (!r) return;
    var track = r.querySelector('[data-addons-track]');
    var nav = r.querySelector('[data-addons-nav]');
    var prev = r.querySelector('[data-addons-prev]');
    var next = r.querySelector('[data-addons-next]');
    if (!track || !nav) return;
    var overflows = track.scrollWidth > track.clientWidth + 2;
    nav.hidden = !overflows;
    if (!overflows) return;
    updateArrows(track, prev, next);
    if (!track.dataset.addonsBound) {
      track.addEventListener('scroll', function () { updateArrows(track, prev, next); }, { passive: true });
      track.dataset.addonsBound = '1';
    }
  }

  /* ---------- Delegated clicks ---------- */

  document.addEventListener('click', function (evt) {
    var addBtn = evt.target.closest('[data-cart-addon]');
    if (addBtn) {
      evt.preventDefault();
      if (addBtn.hasAttribute('disabled')) return;
      var variantId = addBtn.getAttribute('data-variant-id');
      if (variantId) addVariant(variantId, addBtn);
      return;
    }
    var prevBtn = evt.target.closest('[data-addons-prev]');
    if (prevBtn) {
      var t1 = root() && root().querySelector('[data-addons-track]');
      if (t1) t1.scrollBy({ left: -stepAmount(t1), behavior: 'smooth' });
      return;
    }
    var nextBtn = evt.target.closest('[data-addons-next]');
    if (nextBtn) {
      var t2 = root() && root().querySelector('[data-addons-track]');
      if (t2) t2.scrollBy({ left: stepAmount(t2), behavior: 'smooth' });
      return;
    }
  });

  /* ---------- Init on load + when the drawer changes ---------- */

  function boot() {
    preloadRecommendations();
    if (root()) renderAddons(true);
  }
  document.addEventListener('DOMContentLoaded', boot);
  if (document.readyState !== 'loading') boot();

  var drawer = getCartDrawer();
  if (drawer && 'MutationObserver' in window) {
    var obs = new MutationObserver(function (mutations) {
      if (isRendering) return;
      var relevant = false;
      for (var i = 0; i < mutations.length; i++) {
        var t = mutations[i].target;
        if (t && t.closest && t.closest('[data-cart-addons]')) continue;
        relevant = true;
        break;
      }
      if (relevant) scheduleRender();
    });
    obs.observe(drawer, { childList: true, subtree: true });
  }

  window.addEventListener('resize', function () { initAddonsNav(); });
})();
